import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { markChunksHealthy } from "./lib/lazyChunk.js";
import { LocaleProvider } from "./lib/i18n.js";
import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { AuthProvider, useAuth } from "./lib/auth.js";
import { queryClient } from "./lib/trpc.js";
import { router } from "./app/router.js";
import { ToastProvider } from "./components/ui/Toast.js";
import { Logo } from "./components/ui/Logo.js";
import { watchSystemTheme } from "./lib/theme.js";
import { trackViewportHeight } from "./lib/viewportHeight.js";

import "./styles/tokens.css";
import "./styles/base.css";

watchSystemTheme();
// Publishes the genuinely visible height as `--fc-vh`. Never torn down: it
// lives as long as the page does, and the layout depends on it from first paint.
trackViewportHeight();

/**
 * The router's guards need to know who is signed in, but auth state lives in
 * React. This bridges the two by handing the router functions that read the
 * current value rather than a snapshot, so a guard never sees a stale answer.
 */
function RouterBridge() {
  const auth = useAuth();

  if (auth.status === "loading") {
    return <BootSplash />;
  }

  return (
    <RouterProvider
      router={router}
      context={{
        isAuthenticated: () => auth.status === "authenticated",
        isPlatformAdmin: () => Boolean(auth.user?.isPlatformAdmin),
      }}
    />
  );
}

/**
 * Shown only while the silent refresh is in flight — typically a few hundred
 * milliseconds. Deliberately not a spinner: a brief, calm wordmark reads as
 * "loading" without the anxiety of a spinning element.
 */
function BootSplash() {
  return (
    <div
      style={{
        minHeight: "100dvh",
        display: "grid",
        placeItems: "center",
        color: "var(--fc-text-tertiary)",
        fontSize: "var(--fc-text-small)",
        letterSpacing: "var(--fc-tracking-caption)",
      }}
    >
      <Logo size={20} withWordmark />
    </div>
  );
}

/*
 * The app started, so whatever chunk failed last time is no longer failing.
 * Clearing the marker here means a stale chunk met later in the same session
 * can still recover; leaving it set would spend the single retry on the first
 * deploy of the session and leave every later one showing a raw error.
 */
markChunksHealthy();

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    {/* Outermost, because everything below it has words in it. */}
    <LocaleProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ToastProvider>
            <RouterBridge />
          </ToastProvider>
        </AuthProvider>
      </QueryClientProvider>
    </LocaleProvider>
  </StrictMode>,
);
